// frontend/src/features/orders/lib/orderStatus.js
import { getPersianLabel } from '@/lib/persianUi';
import { PAYMENT_MESSAGES } from '../paymentCopy';

// ---- legacy customer-facing labels (kept for back-compat; new UI reads the
// timeline projection instead) ----
export const CUSTOMER_ORDER_STATUS_LABELS = {
  awaiting_payment: 'در انتظار پرداخت',
  awaiting_seller: 'در انتظار تأیید فروشنده',
  preparing: 'در حال آماده‌سازی',
  partially_shipped: 'بخشی ارسال شده',
  shipped: 'ارسال شده',
  delivered: 'تحویل داده شد',
  rejected: 'رد شده توسط فروشنده',
  cancelled: 'لغو شده',
};

// ---- NEW: vendor acceptance axis ----
export const VENDOR_ACCEPTANCE_STATUS_LABELS = {
  pending: 'در انتظار تأیید',
  accepted: 'تأیید شده',
  rejected: 'رد شده',
  auto_cancelled: 'لغو خودکار',
};

// ---- NEW: vendor fulfillment axis (what the vendor does: pick/pack/handover) ----
export const VENDOR_FULFILLMENT_STATUS_LABELS = {
  unfulfilled: 'آماده‌سازی نشده',
  picking: 'در حال برداشت',
  packing: 'در حال بسته‌بندی',
  packed: 'بسته‌بندی شده',
  ready_for_pickup: 'آماده تحویل به پست',
  handed_to_carrier: 'تحویل به پست شده',
  delivered: 'تحویل داده شده',
  returned: 'مرجوع شده',
  lost: 'مفقود شده',
};

// ---- NEW: logistics / shipment axis ----
export const SHIPMENT_STATUS_LABELS = {
  pending: 'در انتظار ارسال',
  in_transit: 'در مسیر',
  out_for_delivery: 'در حال توزیع',
  delivered: 'تحویل شده',
  failed: 'ناموفق',
  returned_to_sender: 'مرجوع به فرستنده',
};

export const ORDER_TRACKING_LABELS = {
  trackingNumber: 'کد رهگیری فروشنده',
  carrier: 'حامل',
  track: 'پیگیری',
};

export const PAYMENT_STATUS_LABELS = {
  unpaid: PAYMENT_MESSAGES.unpaid,
  prepaid_awaiting_cheque_confirmation: PAYMENT_MESSAGES.prepaidAwaitingCheque,
  paid: PAYMENT_MESSAGES.paid,
  cancelled_with_refund_due: PAYMENT_MESSAGES.cancelledWithRefundDue,
  awaiting_payment: PAYMENT_MESSAGES.unpaid,
  verified: PAYMENT_MESSAGES.paid,
  already_verified: PAYMENT_MESSAGES.paid,
  pending: PAYMENT_MESSAGES.pending,
  failed: PAYMENT_MESSAGES.failure,
  expired: PAYMENT_MESSAGES.expired,
  refunded: 'بازپرداخت‌شده',
};

export const SECURE_STATUS_LABELS = {
  awaiting_receipt: PAYMENT_MESSAGES.secureAwaitingReceipt,
  confirmed: PAYMENT_MESSAGES.secureConfirmed,
  not_received: PAYMENT_MESSAGES.secureNotReceived,
};

export const CHEQUE_STATUS_LABELS = {
  awaiting_registration: PAYMENT_MESSAGES.chequeAwaitingRegistration,
  awaiting_confirmation: PAYMENT_MESSAGES.chequeAwaitingConfirmation,
  confirmed: PAYMENT_MESSAGES.chequeConfirmed,
  rejected: PAYMENT_MESSAGES.chequeRejected,
  timed_out: PAYMENT_MESSAGES.chequeTimedOut,
};

const STATUS_BADGE_VARIANTS = {
  awaiting_payment: 'warning',
  awaiting_seller: 'warning',
  preparing: 'info',
  partially_shipped: 'info',
  shipped: 'info',
  delivered: 'success',
  rejected: 'danger',
  cancelled: 'muted',
  pending: 'warning',
  accepted: 'info',
  processing: 'info',
  failed: 'danger',
};

export const getCustomerStatusLabel = (status) =>
  getPersianLabel(CUSTOMER_ORDER_STATUS_LABELS, status, 'orders.customerStatus');

export const getVendorAcceptanceLabel = (status) =>
  getPersianLabel(VENDOR_ACCEPTANCE_STATUS_LABELS, status, 'orders.vendorAcceptance');

export const getVendorFulfillmentLabel = (status) =>
  getPersianLabel(VENDOR_FULFILLMENT_STATUS_LABELS, status, 'orders.vendorFulfillment');

export const getShipmentStatusLabel = (status) =>
  getPersianLabel(SHIPMENT_STATUS_LABELS, status, 'orders.shipmentStatus');

export const getPaymentStatusLabel = (status) =>
  getPersianLabel(PAYMENT_STATUS_LABELS, status, 'orders.paymentStatus');

export const getSecureStatusLabel = (status) =>
  getPersianLabel(SECURE_STATUS_LABELS, status, 'orders.secureStatus');

export const getChequeStatusLabel = (status) =>
  getPersianLabel(CHEQUE_STATUS_LABELS, status, 'orders.chequeStatus');

export const getOrderStatusBadgeClass = (status) =>
  `order-status-badge order-status-badge--${STATUS_BADGE_VARIANTS[status] ?? 'muted'}`;

export const getVendorAxisStatusLabel = (acceptance, fulfillment) =>
  ['pending', 'rejected', 'auto_cancelled'].includes(acceptance)
    ? getVendorAcceptanceLabel(acceptance)
    : getVendorFulfillmentLabel(fulfillment);

export const getVendorAxisBadgeClass = (acceptance, fulfillment) => {
  if (acceptance === 'pending') return getOrderStatusBadgeClass('pending');
  if (acceptance === 'rejected') return getOrderStatusBadgeClass('rejected');
  if (acceptance === 'auto_cancelled') return getOrderStatusBadgeClass('cancelled');
  if (fulfillment === 'delivered') return getOrderStatusBadgeClass('delivered');
  if (fulfillment === 'handed_to_carrier') return getOrderStatusBadgeClass('shipped');
  if (fulfillment === 'lost') return getOrderStatusBadgeClass('failed');
  if (['packing', 'packed', 'ready_for_pickup'].includes(fulfillment)) {
    return getOrderStatusBadgeClass('processing');
  }
  return getOrderStatusBadgeClass('accepted');
};

export const getOrderProgressStep = (status) => {
  switch (status) {
    case 'awaiting_seller': return 1;
    case 'preparing': return 2;
    case 'partially_shipped':
    case 'shipped': return 3;
    case 'delivered': return 4;
    default: return 0;
  }
};